import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, Loader2, CalendarDays, Users, CreditCard, PawPrint,
  Search, TrendingUp, Inbox, Lock,
} from "lucide-react";
import {
  adminLogin, adminMe, adminGetReservations, adminGetStats,
  getToken, setToken, clearToken,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const brl = (n) =>
  Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [logging, setLogging] = useState(false);

  useEffect(() => {
    if (!getToken()) return setAuthed(false);
    adminMe().then(() => setAuthed(true)).catch(() => {
      clearToken();
      setAuthed(false);
    });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    try {
      const { token } = await adminLogin(email.trim(), password);
      setToken(token);
      setAuthed(true);
      toast.success("Bem-vindo(a)!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no login");
    } finally {
      setLogging(false);
    }
  };

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-cream">
        <Loader2 className="w-8 h-8 animate-spin text-brand-forest" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-forest px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-brand-cream p-8 md:p-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-5 h-5 text-brand-terracotta" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-brand-terracotta">
              Área restrita
            </span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-brand-forest mb-1">
            Painel de Reservas
          </h1>
          <p className="text-sm text-brand-ink/60 mb-8">Recanto dos Pinheiros</p>
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-bold text-brand-ink/70">E-mail</Label>
              <Input data-testid="admin-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-bold text-brand-ink/70">Senha</Label>
              <Input data-testid="admin-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            <button
              type="submit"
              disabled={logging}
              data-testid="admin-login-btn"
              className="w-full bg-brand-forest text-brand-cream py-3.5 text-[13px] uppercase tracking-[0.2em] font-bold hover:bg-brand-forest-light transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {logging && <Loader2 className="w-4 h-4 animate-spin" />}
              Entrar
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return <Dashboard onLogout={logout} />;
}

function Dashboard({ onLogout }) {
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    Promise.all([adminGetReservations(), adminGetStats()])
      .then(([r, s]) => {
        setReservations(r);
        setStats(s);
      })
      .catch(() => toast.error("Erro ao carregar reservas"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = reservations.filter((r) => {
    const q = query.toLowerCase();
    return (
      r.fullName?.toLowerCase().includes(q) ||
      r.cabinName?.toLowerCase().includes(q) ||
      r.phone?.toLowerCase().includes(q) ||
      r.reservationId?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-brand-cream">
      {/* Header */}
      <header className="bg-brand-forest text-brand-cream">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-brand-terracotta">Painel administrativo</p>
            <h1 className="text-2xl font-bold tracking-tight">Recanto dos Pinheiros</h1>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 border border-brand-cream/40 px-5 py-2.5 text-[12px] uppercase tracking-[0.15em] font-bold hover:bg-brand-cream hover:text-brand-forest transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          <StatCard icon={Inbox} label="Total de reservas"
            value={stats ? stats.totalReservations : "—"} testid="stat-total" />
          <StatCard icon={TrendingUp} label="Valor estimado (soma)"
            value={stats ? brl(stats.estimatedRevenue) : "—"} testid="stat-revenue" />
        </div>

        <div className="flex items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold tracking-tight text-brand-forest">Solicitações de reserva</h2>
          <div className="relative w-full max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink/40" />
            <Input data-testid="admin-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome, chalé, telefone..." className="pl-9" />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-brand-forest" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-brand-ink/50" data-testid="admin-empty">
            <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nenhuma reserva encontrada.
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-reservations">
            {filtered.map((r) => (
              <motion.div
                key={r.id || r.reservationId}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-brand-forest/15 p-5 md:p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold tracking-[0.15em] text-brand-terracotta bg-brand-terracotta/10 px-2 py-1">
                        #{r.reservationId}
                      </span>
                      <span className="text-lg font-bold text-brand-forest">{r.fullName}</span>
                    </div>
                    <p className="text-sm text-brand-ink/60 mt-1">{r.cabinName}</p>
                  </div>
                  <div className="text-right">
                    <span className="block text-2xl font-bold text-brand-forest">{brl(r.totalPrice)}</span>
                    <span className="text-xs text-brand-ink/50">{r.nights} diária(s)</span>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <Info icon={CalendarDays} label="Período" value={`${r.checkIn} → ${r.checkOut}`} />
                  <Info icon={Users} label="Hóspedes" value={`${r.guests} · Pet: ${r.pet}`} />
                  <Info icon={CreditCard} label="Pagamento"
                    value={r.paymentMethod.toLowerCase().startsWith("cart") ? `Cartão ${r.installments}x` : r.paymentMethod} />
                  <Info icon={PawPrint} label="Nascimento" value={r.birthDate} />
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm mt-3 pt-3 border-t border-brand-forest/10">
                  <Info label="CPF" value={r.cpf} />
                  <Info label="E-mail" value={r.email} />
                  <Info label="WhatsApp" value={
                    <a href={`https://wa.me/55${r.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                      className="text-brand-forest-light underline">{r.phone}</a>
                  } />
                  <Info label="Acompanhante" value={r.companionName || "—"} />
                </div>

                {r.specialRequests && (
                  <div className="mt-3 pt-3 border-t border-brand-forest/10 text-sm">
                    <span className="text-xs uppercase tracking-[0.12em] font-bold text-brand-ink/50">Solicitação especial</span>
                    <p className="text-brand-ink/80 mt-1">{r.specialRequests}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-brand-forest text-brand-cream p-6 flex items-center gap-4" data-testid={testid}>
    <div className="w-12 h-12 flex items-center justify-center bg-brand-cream/10">
      <Icon className="w-6 h-6 text-brand-terracotta" />
    </div>
    <div>
      <span className="block text-xs uppercase tracking-[0.15em] text-brand-cream/60">{label}</span>
      <span className="text-2xl font-bold">{value}</span>
    </div>
  </div>
);

const Info = ({ icon: Icon, label, value }) => (
  <div>
    <span className="flex items-center gap-1.5 text-xs uppercase tracking-[0.1em] font-bold text-brand-ink/45 mb-0.5">
      {Icon && <Icon className="w-3.5 h-3.5" />}{label}
    </span>
    <span className="text-brand-ink/85 break-words">{value}</span>
  </div>
);
